import { useState, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { cn, PaletteUtils } from '@shared/utils';
import { DateRangePickerInput, Icon, Modal, Radio, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import CloseIcon from '@assets/icons/close.svg';

export enum DateRangeTypes {
  LAST_DAY = 'Last day',
  LAST_15_DAYS = 'Last 15 days',
  LAST_30_DAYS = 'Last 30 days',
  LAST_QUARTER = 'Last quarter',
  LAST_YEAR = 'Last year',
  MANUAL = 'Manual',
}

interface IProps {
  className?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  iconClassName?: string;
  label?: ReactNode;
  nullValue?: { isEnable: boolean; text?: string };
  required?: boolean;
  value?: { dateFrom?: Dayjs; dateTo?: Dayjs };
  onChange: (value?: { dateFrom?: Dayjs; dateTo?: Dayjs }) => void;
}

export const SelectDateRange = (props: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>();
  const [isManual, setIsManual] = useState(false);

  const { t } = useTranslation();

  const currentLabel = useMemo(() => {
    if (!props.value || (!props.value.dateFrom && !props.value.dateTo)) return props.nullValue?.text ?? t('selectDate.title');
    return `${props.value.dateFrom?.toDate()?.toLocaleDateString('en-GB') ?? ''} - ${props.value.dateTo?.toDate()?.toLocaleDateString('en-GB') ?? ''}`;
  }, [props.value, t]);

  const onChangeRadio = (value: DateRangeTypes) => {
    const today = dayjs();
    switch (value) {
      case DateRangeTypes.LAST_DAY:
        props.onChange({ dateFrom: today.add(-1, 'day'), dateTo: today });
        break;
      case DateRangeTypes.LAST_15_DAYS:
        props.onChange({ dateFrom: today.add(-15, 'day'), dateTo: today });
        break;
      case DateRangeTypes.LAST_30_DAYS:
        props.onChange({ dateFrom: today.add(-30, 'day'), dateTo: today });
        break;
      case DateRangeTypes.LAST_QUARTER: {
        const quarter = Math.floor(today.month() / 3);
        const startFullQuarter = new Date(today.year(), quarter * 3 - 3, 1);
        const endFullQuarter = new Date(startFullQuarter.getFullYear(), startFullQuarter.getMonth() + 3, 0);
        props.onChange({ dateFrom: dayjs(startFullQuarter), dateTo: dayjs(endFullQuarter) });
        break;
      }
      case DateRangeTypes.LAST_YEAR:
        props.onChange({ dateFrom: today.add(-1, 'year'), dateTo: today });
        break;
      case DateRangeTypes.MANUAL:
        break;
      default:
        break;
    }

    const isManual = value == DateRangeTypes.MANUAL;
    setIsManual(isManual);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.onChange(undefined);
    setSelectedPeriod(undefined);
    setIsManual(false);
  };

  const periodOptions = useMemo(
    () => [
      { text: t('selectDate.periodOptions.lastDay'), value: DateRangeTypes.LAST_DAY },
      { text: t('selectDate.periodOptions.last15Days'), value: DateRangeTypes.LAST_15_DAYS },
      { text: t('selectDate.periodOptions.last30Days'), value: DateRangeTypes.LAST_30_DAYS },
      { text: t('selectDate.periodOptions.lastQuarter'), value: DateRangeTypes.LAST_QUARTER },
      { text: t('selectDate.periodOptions.lastYear'), value: DateRangeTypes.LAST_YEAR },
      { text: t('selectDate.periodOptions.manual'), value: DateRangeTypes.MANUAL },
    ],
    [t],
  );
  return (
    <div className={cn('relative flex flex-col', props.className)}>
      {props.label && (
        <label className='w-full'>
          <Text size='sm' color='secondary'>
            {props.label} {props.required ? ' *' : ''}
          </Text>
        </label>
      )}
      <div
        className={cn(
          'flex h-10 w-full cursor-pointer transition-all duration-200',
          PaletteUtils.isInsigniaPalette &&
            'border border-transparent border-b-light bg-ice transition-all duration-200 ease-in-out hover:border hover:border-primary hover:bg-white dark:bg-soft-black',
          PaletteUtils.isVcfcPalette &&
            'h-12 rounded-[100px] border border-light/70 bg-white transition-all duration-200 ease-in-out hover:border-primary dark:border-none dark:bg-white/10',
          PaletteUtils.isVcfcPalette && 'max-md:justify-between',
          props.wrapperClassName,
        )}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <div className={cn('w-full px-2 py-2', PaletteUtils.isVcfcPalette && 'flex items-center px-4')}>
          <Text
            weight='light'
            className={cn(
              'w-full text-nowrap text-left font-light leading-normal',
              props.value && '!text-soft-black dark:!text-white',
              PaletteUtils.isVcfcPalette && 'text-dark-wet-asphalt dark:text-white/70',
              PaletteUtils.isInsigniaPalette && 'text-dark-wet-asphalt dark:text-gray-dark/70',
            )}
          >
            {currentLabel ?? props.nullValue?.text ?? t('selectDate.title')}
          </Text>
          <div className='relative -top-[1px] right-[3px] flex h-10 w-4 items-center justify-center'>
            {props.value && (props.value.dateFrom || props.value.dateTo) && <Icon icon={<CloseIcon className='h-2 w-2' />} onClick={handleClear} />}
          </div>
        </div>
        <div
          className={cn(
            'relative flex items-center justify-center',
            PaletteUtils.isInsigniaPalette && '-right-[1px] -top-[1px] h-10 min-h-10 w-10 min-w-10 bg-soft-black dark:bg-slate',
            PaletteUtils.isVcfcPalette && 'right-4',
            props.buttonClassName,
          )}
        >
          <Icon
            icon={<ChevronIcon />}
            className={cn('-rotate-90 transition-transform duration-200', isOpen && 'rotate-90', props.iconClassName)}
            color={PaletteUtils.getColor('primary', PaletteUtils.isVcfcPalette ? 'dark' : 'white')}
          />
        </div>
      </div>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={t('selectDate.modals.choosePeriod.title')}>
        <Radio
          orientation='vertical'
          options={periodOptions}
          value={selectedPeriod}
          onChange={(val) => {
            setSelectedPeriod(val);
            onChangeRadio(val as DateRangeTypes);
          }}
        />
        <DateRangePickerInput
          className='m-5'
          dateFrom={props.value?.dateFrom}
          dateTo={props.value?.dateTo}
          readOnly={!isManual}
          onChange={(from, to) => {
            if (from === null || to === null) return;
            props.onChange({ dateFrom: from, dateTo: to });
          }}
        />
      </Modal>
    </div>
  );
};
